import type { Logger } from '@/modules/logger';

/** Per-user pixel ids from the backend (`analyticsSettings` in the sign-in response). */
export type UserAnalyticsSettings = Record<string, { pixelId?: string } | undefined>;

export interface FunnelUser {
  appUserId: string;
  accessToken: string;
  email?: string;
  country?: string;
  city?: string;
  analyticsSettings?: UserAnalyticsSettings;
}

export const USER_STORAGE_KEY = '[[user]]-user';

export class QuizUserSession {
  user: FunnelUser = { appUserId: 'anonymous', accessToken: '' };

  constructor(
    private readonly apiUrl: string | undefined,
    private readonly productName: string,
    private readonly onboardingVariant: string,
    private readonly logger: Logger,
  ) {}

  async init(): Promise<FunnelUser> {
    const saved = this.readSaved();
    if (saved && (!this.apiUrl || (await this.isValid(saved)))) {
      Object.assign(this.user, saved);
      return this.user;
    }
    if (!this.apiUrl) return this.user;

    try {
      const url = new URL(`${this.apiUrl.replace(/\/$/, '')}/v1/auth/anonymous/sign-in`);
      url.searchParams.set('source', 'web');
      url.searchParams.set('userAgent', navigator.userAgent);
      url.searchParams.set('slug', this.onboardingVariant);
      const response = await fetch(url, {
        headers: { 'x-app-type': this.productName, 'x-landing': this.productName },
      });
      if (!response.ok) throw new Error(`User session request failed with HTTP ${response.status}`);
      const data = (await response.json()) as {
        token: string;
        userId: string;
        country?: string;
        city?: string;
        analyticsSettings?: UserAnalyticsSettings;
      };
      Object.assign(this.user, {
        appUserId: data.userId,
        accessToken: data.token,
        country: data.country,
        city: data.city,
        analyticsSettings: data.analyticsSettings,
      });
      sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(this.user));
    } catch (error) {
      this.logger.logWarning({ error: String(error) }, 'userSessionInitFailed');
    }
    return this.user;
  }

  /** `GET /v1/me` — saved token is still valid and belongs to the same user. */
  private async isValid(saved: FunnelUser): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiUrl!.replace(/\/$/, '')}/v1/me`, {
        headers: {
          'x-session-token': saved.accessToken,
          'x-app-type': this.productName,
          'x-landing': this.productName,
        },
      });
      if (!response.ok) return false;
      const me = (await response.json()) as { id?: string };
      return me?.id === saved.appUserId;
    } catch (error) {
      this.logger.logWarning({ error: String(error) }, 'userSessionValidateFailed');
      return false;
    }
  }

  private readSaved(): FunnelUser | undefined {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(USER_STORAGE_KEY) || 'null') as Partial<FunnelUser> | null;
      if (parsed?.appUserId && parsed.accessToken) return parsed as FunnelUser;
    } catch (error) {
      this.logger.logWarning({ error: String(error) }, 'userSessionReadFailed');
    }
    return undefined;
  }
}

export const readSavedFunnelUser = (): FunnelUser => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(USER_STORAGE_KEY) || 'null') as Partial<FunnelUser> | null;
    if (parsed?.appUserId && parsed.accessToken) return parsed as FunnelUser;
  } catch {
    // The caller can continue with an anonymous session.
  }
  return { appUserId: 'anonymous', accessToken: '' };
};

/**
 * Preview only (WebPaywalls parity): KA generates an onboarding with its own user, and the paywall pays as
 * that user — the gateway refuses a payment from a user without an onboarding (422). `undefined` on failure.
 */
export const loadPreviewFunnelUser = async (
  apiUrl: string | undefined,
  productName: string,
  onboardingId: string,
  logger: Logger,
  fetchImpl: typeof fetch = (...args) => fetch(...args),
  storage: Storage = sessionStorage,
): Promise<FunnelUser | undefined> => {
  if (!apiUrl) return undefined;
  try {
    const response = await fetchImpl(`${apiUrl.replace(/\/$/, '')}/v1/onboarding/${encodeURIComponent(onboardingId)}`, {
      headers: { Accept: 'application/json', 'x-app-type': productName, 'x-landing': productName },
    });
    if (!response.ok) throw new Error(`Preview onboarding request failed with HTTP ${response.status}`);
    const data = (await response.json()) as {
      appUserId?: string;
      appUserToken?: string;
      email?: string;
      country?: string;
      city?: string;
      analyticsSettings?: UserAnalyticsSettings;
    } | null;
    if (!data?.appUserId || !data.appUserToken) throw new Error('Preview onboarding has no user');
    const user: FunnelUser = {
      appUserId: data.appUserId,
      accessToken: data.appUserToken,
      email: data.email,
      country: data.country,
      city: data.city,
      analyticsSettings: data.analyticsSettings,
    };
    storage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    logger.logInfo({ onboardingId }, 'previewUserLoaded');
    return user;
  } catch (error) {
    logger.logWarning({ onboardingId, error: String(error) }, 'previewUserFailed');
    return undefined;
  }
};
